'use client';
import { __rest } from '../../node_modules/tslib/tslib.es6.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import styled, { css } from 'styled-components';
import { switchProp } from 'styled-tools';
import { isExternalLink } from '../../helpers/isExternalLink.js';
import { withTitan } from '../../hocs/withTitan/withTitan.js';
import { defaultStyles } from '../../mixins/defaultStyles.js';
import { spaceProp } from '../../mixins/spaceProp.js';
import { textStyle } from '../../mixins/textStyle.js';
import { useTitanRouter } from '../../router/hooks/useTitanRouter.js';

const StyledButton = styled.a.withConfig({
  shouldForwardProp: (prop, defaultValidationFn) => defaultValidationFn(prop)
}).withConfig({
  componentId: "sc-kyi5ih-0"
})(["", ";", ";", ";position:relative;outline:none;display:inline-flex;align-items:center;text-decoration:none;border:0;line-height:1.25;border-radius:", ";&:hover{cursor:pointer;}&:disabled{pointer-events:none;}svg{flex:0 0 auto;}", " ", " ", " ", ""], textStyle('label'), defaultStyles.fontSmoothing, defaultStyles.boxSizing, ({
  theme
}) => theme.components.button.borderRadius, ({
  theme
}) => switchProp('variant', {
  primary: css(["background-color:", ";color:", ";box-shadow:inset 0 0 0 1px ", ";&:hover{background-color:", ";box-shadow:inset 0 0 0 1px ", ",", ";}&:focus{box-shadow:inset 0 0 0 1px ", ",inset 0 0 0 2px ", ";}&:disabled{color:", ";background-color:", ";box-shadow:inset 0 0 0 1px ", ";}"], theme.components.button.variants.primary.backgroundColor, theme.components.button.variants.primary.textColor, theme.components.button.variants.primary.border, theme.components.button.variants.primary.backgroundColorHover, theme.components.button.variants.primary.borderHover, theme.components.button.boxShadowHover, theme.components.button.variants.primary.borderFocus, theme.components.button.variants.primary.borderFocusInset, theme.components.button.variants.primary.textColorDisabled, theme.components.button.variants.primary.backgroundColorDisabled, theme.components.button.variants.primary.borderDisabled),
  secondary: css(["background-color:", ";color:", ";box-shadow:inset 0 0 0 1px ", ";&:hover{background-color:", ";box-shadow:inset 0 0 0 1px ", ",", ";}&:focus{box-shadow:inset 0 0 0 1px ", ",inset 0 0 0 2px ", ";}&:disabled{color:", ";background-color:", ";box-shadow:inset 0 0 0 1px ", ";}"], theme.components.button.variants.secondary.backgroundColor, theme.components.button.variants.secondary.textColor, theme.components.button.variants.secondary.border, theme.components.button.variants.secondary.backgroundColorHover, theme.components.button.variants.secondary.borderHover, theme.components.button.boxShadowHover, theme.components.button.variants.secondary.borderFocus, theme.components.button.variants.secondary.borderFocusInset, theme.components.button.variants.secondary.textColorDisabled, theme.components.button.variants.secondary.backgroundColorDisabled, theme.components.button.variants.secondary.borderDisabled),
  clear: css(["background-color:", ";color:", ";text-decoration:", ";box-shadow:inset 0 0 0 1px ", ";&:hover{color:", ";text-decoration:", ";box-shadow:inset 0 0 0 1px ", ";}&:focus{color:", ";text-decoration:", ";box-shadow:inset 0 0 0 1px ", ",inset 0 0 0 1px ", ";}&:disabled{text-decoration:", ";color:", ";background-color:", ";box-shadow:inset 0 0 0 1px ", ";}"], theme.components.button.variants.clear.backgroundColor, theme.components.button.variants.clear.textColor, theme.components.button.variants.clear.textDecoration, theme.components.button.variants.clear.border, theme.components.button.variants.clear.textColorHover, theme.components.button.variants.clear.textDecorationHover, theme.components.button.variants.clear.borderHover, theme.components.button.variants.clear.textColorFocus, theme.components.button.variants.clear.textDecorationFocus, theme.components.button.variants.clear.borderFocus, theme.components.button.variants.clear.borderFocusInset, theme.components.button.variants.clear.textDecoration, theme.components.button.variants.clear.textColorDisabled, theme.components.button.variants.clear.backgroundColorDisabled, theme.components.button.variants.clear.borderDisabled),
  danger: css(["background-color:", ";color:", ";box-shadow:inset 0 0 0 1px ", ";&:hover{background-color:", ";box-shadow:inset 0 0 0 1px ", ",", ";}&:focus{box-shadow:inset 0 0 0 1px ", ",inset 0 0 0 2px ", ";}&:disabled{color:", ";background-color:", ";box-shadow:inset 0 0 0 1px ", ";}"], theme.components.button.variants.danger.backgroundColor, theme.components.button.variants.danger.textColor, theme.components.button.variants.danger.border, theme.components.button.variants.danger.backgroundColorHover, theme.components.button.variants.danger.borderHover, theme.components.button.boxShadowHover, theme.components.button.variants.danger.borderFocus, theme.components.button.variants.danger.borderFocusInset, theme.components.button.variants.danger.textColorDisabled, theme.components.button.variants.danger.backgroundColorDisabled, theme.components.button.variants.danger.borderDisabled)
}), ({
  theme
}) => switchProp('size', {
  S: css(["padding:", ";"], theme.components.button.sizes.S.padding),
  M: css(["padding:", ";"], theme.components.button.sizes.M.padding),
  L: css(["padding:", ";", ";"], theme.components.button.sizes.L.padding, textStyle('labelLarge'))
}), ({
  isFullWidth
}) => isFullWidth && css(["width:100%;justify-content:center;"]), ({
  spaceAfter
}) => spaceProp(spaceAfter));
const ButtonText = styled.span.withConfig({
  componentId: "sc-kyi5ih-1"
})(["", " ", ""], ({
  hasIconLeft,
  theme
}) => hasIconLeft && css(["padding-left:", ";"], theme.components.button.paddingIcon), ({
  hasIconRight,
  theme
}) => hasIconRight && css(["padding-right:", ";"], theme.components.button.paddingIcon));
const Button = /*#__PURE__*/withTitan((_a, ref) => {
  var {
      href,
      iconLeft,
      iconRight,
      type = 'button',
      children,
      variant = 'primary',
      size = 'M',
      isDisabled,
      testId = 'button'
    } = _a,
    rest = __rest(_a, ["href", "iconLeft", "iconRight", "type", "children", "variant", "size", "isDisabled", "testId"]);
  const {
    linkWrapper
  } = useTitanRouter();
  const renderAs = href && !isDisabled ? 'a' : 'button';
  const rel = href && isExternalLink(href) ? 'noopener noreferrer' : undefined;
  const target = href && isExternalLink(href) ? '_blank' : undefined;
  const button = jsxs(StyledButton, Object.assign({
    ref: ref,
    as: renderAs,
    "data-test-id": testId,
    disabled: isDisabled,
    href: href,
    rel: rel,
    size: size,
    target: target,
    type: href ? undefined : type,
    variant: variant
  }, rest, {
    children: [iconLeft, children && jsx(ButtonText, {
      hasIconLeft: !!iconLeft,
      hasIconRight: !!iconRight,
      children: children
    }), iconRight]
  }));
  if (href) {
    return linkWrapper(button);
  }
  return button;
}, {
  name: 'Button'
});

export { Button, StyledButton };
//# sourceMappingURL=Button.js.map
